import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            const token = localStorage.getItem('adminToken');

            if (!token) {
                navigate('/admin/login');
                return;
            }

            try {
                const response = await fetch(`${API_URL}/api/auth/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminUser');
                    navigate('/admin/login');
                    return;
                }

                const admin = await response.json();
                localStorage.setItem('adminUser', JSON.stringify(admin));

                if (admin.mustChangePassword && location.pathname !== '/admin/change-password') {
                    navigate('/admin/change-password', { replace: true });
                    return;
                }

                setReady(true);
            } catch (error) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                navigate('/admin/login');
            }
        };

        setReady(false);
        validateToken();
    }, [navigate, location.pathname, API_URL]);

    const logout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const isActive = (path) => {
        return location.pathname === path ? 'bg-gray-700' : '';
    };

    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-3xl mb-3"></i>
                    <p>Checking access...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-gray-100">
            {/* Sidebar */}
            <div className="bg-gray-800 text-white w-64 p-6 min-h-screen">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold">VishwaVoyagerz</h1>
                    <p className="text-gray-400 text-sm">Admin Panel</p>
                </div>

                <nav className="space-y-2">
                    <Link
                        to="/admin/dashboard"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/dashboard')}`}
                    >
                        <i className="fas fa-tachometer-alt mr-2"></i>Dashboard
                    </Link>
                    <Link
                        to="/admin/tours"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/tours')}`}
                    >
                        <i className="fas fa-map-marked-alt mr-2"></i>Tours
                    </Link>
                    <Link
                        to="/admin/categories"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/categories')}`}
                    >
                        <i className="fas fa-tags mr-2"></i>Categories
                    </Link>
                    <Link
                        to="/admin/villas"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/villas')}`}
                    >
                        <i className="fas fa-home mr-2"></i>Villas & Bungalows
                    </Link>
                    <Link
                        to="/admin/bookings"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/bookings')}`}
                    >
                        <i className="fas fa-calendar-check mr-2"></i>Bookings
                    </Link>
                    <Link
                        to="/admin/contacts"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/contacts')}`}
                    >
                        <i className="fas fa-envelope mr-2"></i>Contacts
                    </Link>
                    <Link
                        to="/admin/communication"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/communication')}`}
                    >
                        <i className="fas fa-comments mr-2"></i>Communication
                    </Link>
                    <Link
                        to="/admin/email-settings"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/email-settings')}`}
                    >
                        <i className="fas fa-cog mr-2"></i>Email Settings
                    </Link>
                    <Link
                        to="/admin/newsletter"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/newsletter')}`}
                    >
                        <i className="fas fa-newspaper mr-2"></i>Newsletter
                    </Link>
                    <Link
                        to="/admin/site-settings"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/site-settings')}`}
                    >
                        <i className="fas fa-sliders-h mr-2"></i>Site Settings
                    </Link>
                    <Link
                        to="/admin/security"
                        className={`block py-2 px-4 rounded hover:bg-gray-700 ${isActive('/admin/security')}`}
                    >
                        <i className="fas fa-shield-alt mr-2"></i>Security
                    </Link>
                    <button
                        onClick={logout}
                        className="block w-full text-left py-2 px-4 rounded hover:bg-gray-700 text-red-400"
                    >
                        <i className="fas fa-sign-out-alt mr-2"></i>Logout
                    </button>
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-8">
                <Outlet />
            </div>
        </div>
    );
};

export default AdminLayout;
